import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import ZKAnchorBadge, { ZKAnchorStatus } from './ZKAnchorBadge';
import { txUrl, blockUrl, shortHash, getChainLabel } from '../lib/chainExplorer';

export interface AnchorRow {
  proofId: string;
  submitterDid?: string;
  holderDid?: string | null;
  onBehalfOfDid?: string | null;
  credentialId?: string;
  credentialType?: string;
  predicateName?: string;
  predicateHash?: string;
  txHash: string;
  blockNumber: number | null;
  status: ZKAnchorStatus | string;
  submittedAt: string;
  confirmedAt?: string | null;
  revokedAt?: string | null;
  revokedBlock?: number | null;
  kind?: 'self' | 'delegated';
}

interface Props {
  /** Filter by submitter or holder DID. */
  did?: string;
  /** When set, append `?did=<central>` to the dashboard query. */
  centralDid?: string;
  /** Limit (default 20). */
  limit?: number;
  /** When true, show "Delegated proofs received" filter pills. */
  enableKindFilter?: boolean;
  /** Override the API path; defaults to `/dashboard/zk-anchors`. */
  endpoint?: string;
  /** Heading override. */
  heading?: string;
}

interface PaginatedShape {
  items?: AnchorRow[];
  totalCount?: number;
  data?: AnchorRow[];
}

/**
 * Right-rail timeline panel showing on-chain anchor activity for a DID.
 *
 * Hits the dashboard feed (`GET /api/v1/dashboard/zk-anchors`) when no
 * `endpoint` override is supplied. Hidden gracefully when there are no
 * anchors yet for the given DID.
 */
export default function ZKAnchorTimeline({
  did,
  centralDid,
  limit = 20,
  enableKindFilter = false,
  endpoint,
  heading,
}: Props) {
  const { t } = useTranslation('trust');
  const [items, setItems] = useState<AnchorRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<'all' | 'self' | 'delegated'>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    const filterDid = centralDid ?? did;
    if (filterDid) params.set('did', filterDid);
    if (limit) params.set('limit', String(limit));
    const path = `${endpoint ?? '/dashboard/zk-anchors'}${params.toString() ? '?' + params.toString() : ''}`;
    apiFetch<PaginatedShape | AnchorRow[]>(path)
      .then((res) => {
        if (cancelled) return;
        const list: AnchorRow[] = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
            ? res.items as AnchorRow[]
            : Array.isArray(res?.data)
              ? res.data as AnchorRow[]
              : [];
        setItems(list);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'failed');
        setItems([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [did, centralDid, limit, endpoint]);

  if (loading) {
    return (
      <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
        <h3 className="mb-3 text-sm font-bold text-text-main">{heading ?? t('zkTimeline.heading')}</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
        <h3 className="mb-3 text-sm font-bold text-text-main">{heading ?? t('zkTimeline.heading')}</h3>
        <p className="text-xs text-red-600">{t('zkTimeline.errorLoad')}</p>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return null;
  }

  // Apply client-side `kind` filter as a fallback if the API ignores the param.
  const filteredItems = items.filter((row) => {
    if (kindFilter === 'all') return true;
    const kind = row.kind ?? (row.onBehalfOfDid || row.holderDid ? 'delegated' : 'self');
    return kind === kindFilter;
  });

  return (
    <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-text-main">{heading ?? t('zkTimeline.heading')}</h3>
          <p className="mt-1 text-xs text-text-faint">
            {t('zkTimeline.subheading', { chain: getChainLabel() })}
          </p>
        </div>
        {enableKindFilter && (
          <div className="flex gap-1 rounded-md border border-border-subtle bg-slate-50 p-0.5 text-[11px]">
            {(['all', 'self', 'delegated'] as const).map((k) => (
              <button
                key={k}
                onClick={() => setKindFilter(k)}
                className={`rounded px-2 py-1 font-bold transition-colors ${
                  kindFilter === k ? 'bg-white text-primary shadow-sm' : 'text-text-faint hover:text-text-main'
                }`}
              >
                {t(`zkTimeline.kind.${k}`)}
              </button>
            ))}
          </div>
        )}
      </div>

      {filteredItems.length === 0 ? (
        <p className="py-6 text-center text-xs text-text-faint">{t('zkTimeline.emptyForFilter')}</p>
      ) : (
        <ul className="space-y-3">
          {filteredItems.map((row) => {
            const tx = txUrl(row.txHash);
            const block = blockUrl(row.blockNumber);
            const status = row.status as ZKAnchorStatus;
            return (
              <li key={`${row.proofId}-${row.txHash}`} className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary" style={{ fontSize: 16 }}>token</span>
                      <span className="truncate text-sm font-bold text-text-main">
                        {row.predicateName || row.credentialType || t('zkTimeline.proofGeneric')}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-text-faint">
                      <span>{formatRelative(row.submittedAt)}</span>
                      {row.blockNumber != null && (
                        block ? (
                          <a href={block} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                            {t('zkTimeline.atBlock', { block: row.blockNumber })}
                          </a>
                        ) : (
                          <span>{t('zkTimeline.atBlock', { block: row.blockNumber })}</span>
                        )
                      )}
                      {row.txHash && (
                        tx ? (
                          <a href={tx} target="_blank" rel="noopener noreferrer" className="font-mono text-primary hover:underline">
                            {shortHash(row.txHash)}
                          </a>
                        ) : (
                          <span className="font-mono">{shortHash(row.txHash)}</span>
                        )
                      )}
                      {row.kind === 'delegated' && (
                        <span className="rounded-full border border-blue-100 bg-blue-50 px-1.5 py-0.5 font-bold uppercase text-[9px] text-blue-700">
                          {t('zkTimeline.kind.delegated')}
                        </span>
                      )}
                    </div>
                  </div>
                  <ZKAnchorBadge status={status} blockNumber={row.blockNumber} revokedBlock={row.revokedBlock} compact />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function formatRelative(iso: string): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffMs = Date.now() - t;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}
